// Why an agent run ended, turned into something an operator can read.
//
// The reason is the loop's OWN record (SolidLoop::Loop#error_message), written
// by the gem as "<ErrorClass>: <message>" — e.g.
// "SolidLoop::LimitExceededError: Safety limit hit: max_total_tokens". The class
// name means nothing to an operator, so we lead with a plain-English headline;
// callers ALWAYS reproduce `raw` underneath verbatim, so an unrecognised message
// is never swallowed and no reason the payload doesn't carry is ever invented.
//
// Shared by the LIVE panel (AgentRunPanel) and the read-only archive
// (RunArchiveView) so reloading a run cannot downgrade the explanation back to
// the bare gem string.

// The gem's four safety limits (Middlewares::AgentInitialization#check_limits!).
// Object.create(null) — a plain object literal would answer for "constructor",
// "__proto__", "toString"…, and a message naming one of those would render
// inherited junk ("function Object() { [native code] }") as if it were our copy.
// This block exists to never invent text; the lookup must not be able to.
const SAFETY_LIMIT_LABELS = Object.assign(Object.create(null), {
  max_total_tokens: "the token ceiling (prompt + completion, including cached tokens)",
  max_cost: "the cost budget",
  max_steps: "the step ceiling",
  max_duration: "the working-time limit (generation + tools, excluding time paused)"
});

// ANCHORED at the gem's exception class — the same marker
// AgentRuns::LimitStopReconciler keys on. `error_message` can carry remote
// provider text (the gem slices a failing response body straight into it), so an
// unanchored match would let an echoed phrase anywhere in a provider's JSON
// trigger the confident "the run is over, its counters are cumulative" story.
const LIMIT_STOP = /^SolidLoop::LimitExceededError:\s*Safety limit hit:\s*([A-Za-z0-9_]+)/;
const USER_STOP = /^Stopped by user\b/i;
// Only the leading "Some::ErrorClass: " marker is dropped from the headline of
// an unrecognised message; the full original still shows below.
const ERROR_CLASS_PREFIX = /^[A-Za-z0-9_:]*Error:\s*/;
// How much of an unrecognised message the HEADLINE may quote before it stops
// being a headline. The verbatim copy underneath is never truncated.
const HEADLINE_MAX = 160;

function describeStopReason(raw) {
  const text = (raw ?? "").toString().trim();
  if (!text) {
    return null;
  }

  const limit = LIMIT_STOP.exec(text);
  if (limit) {
    const label = SAFETY_LIMIT_LABELS[limit[1]] || `the "${limit[1]}" limit`;
    return {
      kind: "limit",
      headline: `Stopped by a safety limit — this run reached ${label}.`,
      // Owner decision (2026-07-28): a limit stop is OVER. The counters are
      // cumulative, so resuming the same loop would re-trip the ceiling on the
      // next turn — hence a fresh run, never a Continue.
      detail:
        "The run is over — its counters are cumulative, so continuing this loop " +
        "would hit the same limit again on the next turn. Raise the limit for the " +
        "model in LLM Settings, or start a fresh run.",
      raw: text
    };
  }

  if (USER_STOP.test(text)) {
    return { kind: "user", headline: "You stopped this run.", detail: null, raw: text };
  }

  // A headline is a summary. `error_message` can be an 800-character minified
  // provider body, and repeating all of it here — with `raw` repeating it again
  // below — turns the block into two walls of gibberish. So the headline takes
  // the FIRST LINE, bounded; `raw` still carries every character verbatim, so
  // nothing is swallowed, it just is not said twice.
  const summary = text.replace(ERROR_CLASS_PREFIX, "").split("\n", 1)[0].trim();
  const short = summary.length > HEADLINE_MAX ? `${summary.slice(0, HEADLINE_MAX)}…` : summary;
  return {
    kind: "error",
    headline: short ? `This run ended with an error: ${short}` : "This run ended with an error.",
    detail: null,
    raw: text
  };
}

export { describeStopReason };
